import { useCallback, useEffect, useState } from "react";
import React from "react";
import { Col, FormCheck, FormControl, FormLabel, Row, Spinner } from "react-bootstrap";
import { Helmet } from "react-helmet";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import DstButton from "../../components/DstButton/DstButton";
import DstConfirm from "../../components/DstConfirm/DstConfirm";
import CheckIcon from "../../components/DstIcons/CheckIcon";
import CrossIcon from "../../components/DstIcons/CrossIcon";
import DstTabs from "../../components/DstTabs/DstTabs";
import MultiSelectLiveDropdown from "../../components/MultiSelectLiveDropdown/MultiSelectLiveDropdown";
import Popup from "../../components/Popup/Popup";
import UserBlock from "../../components/UserBlock/UserBlock";
import { useSession } from "../../contexts/SessionContext";
import { Note } from "../../models/Note";
import { TabData } from "../../models/TabModel";
import ApiService from "../../services/ApiService";
import { validateUserPermission } from "../../services/PermissionGuard";
import { getUTCTime } from "../../services/TimeService";
import translate from "../../services/Translate";
import {
  displayDate,
  fetchFlags,
  loginAs,
  logout,
  refreshUserCohorts,
  refreshUserDetails,
  refreshUserFlags,
  refreshUserLessons,
  updateUser,
} from "./User.function";
import "./User.scss";
import UserActivity from "./components/UserActivity/UserActivity";
import UserCertification from "./components/UserCertification/UserCertification";
import UserDiplomation from "./components/UserDiplomation/UserDiplomation";
import UserDocuments from "./components/UserDocuments/UserDocuments";
import UserEmails from "./components/UserEmails/UserEmails";
import UserMeetings from "./components/UserMeetings/UserMeetings";
import UserMessages from "./components/UserMessages/UserMessages";
import UserNotes from "./components/UserNotes/UserNotes";
import UserOverview from "./components/UserOverview/UserOverView";
import UserProjects from "./components/UserProjects/UserProjects";
import UserSurveyAnswers from "./components/UserSurveyAnswers/UserSurveyAnswers";
import UserTutors from "./components/UserTutors/UserTutors";

const initialLoadedState = {
  user: false,
  cohorts: false,
  projects: false,
  lessons: false,
  emails: false,
  documents: false,
  flags: false,
  meetings: false,
  exams: false,
  certifs: false,
  notes: false,
  messages: false,
  advancements: false,
};

const User = () => {
  const { language, user_id, username, permissions, tutorred_users } = useSession();
  const navigate = useNavigate();

  const [loaded, setLoaded] = useState(initialLoadedState);
  // Core
  const [userData, setUserData] = useState({});
  const params = useParams();
  const refreshUserExams = async () => {
    try {
      const response = await ApiService.get(`users/${params.user_id}/evaluation_attempts?fetch_corrector=true`);
      setUserData((userData) => ({
        ...userData,
        user_exams: response.data.evaluation_attempts,
      }));
      setLoaded((loaded) => ({ ...loaded, exams: true }));
    } catch (error) {
      console.error("An error occurred for fetching user's exams", error);
    }
  };

  const refreshUserCertifications = async () => {
    try {
      const response = await ApiService.get(`users/${params.user_id}/certifications`);
      setUserData((prevData) => ({
        ...prevData,
        user_certifs: response.data.certifications,
      }));
      setLoaded((prevLoaded) => ({
        ...prevLoaded,
        certifs: true,
      }));
    } catch (error) {
      console.error("Failed to fetch certifications", error);
    }
  };

  const refreshUserDocuments = useCallback(async () => {
    setLoaded((prevLoaded) => ({
      ...prevLoaded,
      documents: false,
    }));
    try {
      const response = await ApiService.get(`users/${params.user_id}/documents`);
      setUserData((prevData) => ({
        ...prevData,
        user_documents: response.data.documents,
      }));
    } catch (error) {
      console.error("An error occurred for fetching user's documents", error);
    } finally {
      setLoaded((prevLoaded) => ({
        ...prevLoaded,
        documents: true,
      }));
    }
  }, [params.user_id]);

  const handleRefreshUserLessons = () => refreshUserLessons(params.user_id!, setUserData, setLoaded);

  const _REMOVE_USER_NOTE_KEYS = [
    { id: "deployment_mistake", key: "PAGES.USER.USER_BLOCK.REMOVE_CONFIRM.REASONS.DEPLOYMENT_MISTAKE" },
    { id: "learner_dropped", key: "PAGES.USER.USER_BLOCK.REMOVE_CONFIRM.REASONS.LEARNER_DROPPED" },
    { id: "contract_breach", key: "PAGES.USER.USER_BLOCK.REMOVE_CONFIRM.REASONS.CONTRACT_BREACH" },
    { id: "cohort_change", key: "PAGES.USER.USER_BLOCK.REMOVE_CONFIRM.REASONS.COHORT_CHANGE" },
    { id: "other", key: "PAGES.USER.USER_BLOCK.REMOVE_CONFIRM.REASONS.OTHER" },
  ];

  // Edition
  const [showEditionModale, setShowEditionModale] = useState(false);
  const [flags, setFlags] = useState<any>([]);
  const [editedUserData, setEditedUserData] = useState({});
  const [modaleLoaded, setModaleLoaded] = useState(false);
  const [editionLoading, setEditionLoading] = useState(false);
  const [activeKey, setActiveKey] = useState("overview");
  const [activeModalKey, setActiveModalKey] = useState("information");
  const [activeNote, setActiveNote] = useState<Note | null>(null);
  const [editionReady, setEditionReady] = useState(false);
  const [showRemoveUsersFromCohortConfirm, setShowRemoveUsersFromCohortConfirm] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const state = location.state as { tab?: string } | null;
    if (state?.tab) {
      setActiveKey(state.tab);
    }
  }, [location.state]);

  const _build_tabs = (user_data: any) => {
    const tabsData = [
      {
        id: "overview",
        title: translate(language, "PAGES.USER.TAB.OVERVIEW"),
        content: (
          <UserOverview
            language={language}
            setActiveKey={setActiveKey}
            user_data={user_data}
            lessonsLoaded={loaded.lessons}
            notesLoaded={loaded.notes}
            certifsLoaded={loaded.certifs}
            setActiveNote={setActiveNote}
            refreshUserLessons={handleRefreshUserLessons}
          />
        ),
      },
      {
        id: "activity",
        title: translate(language, "PAGES.USER.TAB.ACTIVITY"),
        content: (
          <UserActivity
            language={language}
            userName={user_data.first_name}
            userId={Number(params.user_id)}
            userCohorts={user_data.user_cohorts || []}
            cohortsLoaded={loaded.cohorts}
            userModules={user_data?.user_modules}
            userModulesCohort={user_data?.user_sprints}
            canReadLogs={validateUserPermission({ methods: ["GET"], route: "/users/*/logs" }, permissions)}
            canReadLogtime={validateUserPermission({ methods: ["GET"], route: "/users/*/logtime" }, permissions)}
          />
        ),
      },
      validateUserPermission({ methods: ["GET"], route: "/users/*/certifications" }, permissions) ||
      validateUserPermission({ methods: ["GET"], route: "/users/*/evaluation_attempts" }, permissions)
        ? {
            id: "certif-exams",
            title: "Certif & Exams",
            content: (
              <UserCertification
                language={language}
                userData={user_data}
                userId={Number(params.user_id)}
                certifsLoaded={loaded.certifs}
                refreshUserCertifications={refreshUserCertifications}
                canReadExams={validateUserPermission(
                  { methods: ["GET"], route: "/users/*/evaluation_attempts" },
                  permissions
                )}
              />
            ),
          }
        : undefined,
      validateUserPermission({ methods: ["GET"], route: "/users/*/emails" }, permissions)
        ? {
            id: "emails",
            title: translate(language, "PAGES.USER.TAB.EMAIL"),
            content: <UserEmails userId={Number(params.user_id)} userEmail={user_data.email} language={language} />,
          }
        : undefined,
      validateUserPermission([{ methods: ["GET"], route: "/users/*/documents" }], permissions)
        ? {
            id: "documents",
            title: translate(language, "PAGES.USER.TAB.DOCUMENT"),
            content: (
              <UserDocuments
                userId={Number(params.user_id)}
                language={language}
                refreshUserDocuments={refreshUserDocuments}
              />
            ),
          }
        : undefined,
      validateUserPermission([{ methods: ["GET"], route: "/users/*/meetings" }], permissions)
        ? {
            id: "meetings",
            title: translate(language, "PAGES.USER.TAB.MEETING"),
            content: <UserMeetings language={language} userId={Number(params.user_id)} />,
          }
        : undefined,
      validateUserPermission({ methods: ["GET"], route: "/users/*/projects" }, permissions)
        ? {
            id: "projects",
            title: translate(language, "PAGES.USER.TAB.PROJECT"),
            content: (
              <UserProjects
                key={user_data.user_cohorts?.map((cohort: any) => cohort.id).join("-")}
                language={language}
                userId={Number(params.user_id)}
              />
            ),
          }
        : undefined,
      validateUserPermission([{ methods: ["GET"], route: "/users/*/notes" }], permissions)
        ? {
            id: "notes",
            title: translate(language, "PAGES.USER.TAB.NOTE"),
            content: (
              <UserNotes
                language={language}
                user_data={user_data}
                setUserData={setUserData}
                user_name={username!}
                loaded={loaded.notes}
                activeNote={activeNote}
                setActiveNote={setActiveNote}
              />
            ),
          }
        : undefined,
      validateUserPermission([{ methods: ["GET"], route: "/users/*/messages" }], permissions)
        ? {
            id: "messages",
            title: translate(language, "PAGES.USER.TAB.MESSAGES"),
            content: (
              <div className="w-100">
                <UserMessages language={language} userId={Number(params.user_id)} />
              </div>
            ),
          }
        : undefined,
      validateUserPermission({ methods: ["GET"], route: "/users/*/cohorts/advancements" }, permissions)
        ? {
            id: "diplomation",
            title: translate(language, "PAGES.USER.TAB.DIPLOMATION"),
            content: <UserDiplomation language={language} userId={Number(params.user_id)} />,
          }
        : undefined,
      validateUserPermission({ methods: ["GET"], route: "/users/*/tutors" }, permissions) ||
      tutorred_users?.map((tutorred_user) => tutorred_user.user_id).includes(Number(params.user_id))
        ? {
            id: "tutors",
            title: translate(language, "PAGES.USER.TAB.TUTORS"),
            content: (
              <UserTutors
                key={Number(params.user_id)}
                language={language}
                tutorredCohortIds={
                  tutorred_users
                    ?.filter((tutorred_user) => tutorred_user.user_id === Number(params.user_id))
                    ?.map((tutorred_user) => tutorred_user.cohort_id) || []
                }
                userId={Number(params.user_id)}
                userApprenticeshipCohorts={(user_data.user_cohorts || []).filter(
                  (cohort: any) => cohort.customer_type === "apprenticeship"
                )}
              />
            ),
          }
        : undefined,
      validateUserPermission([{ methods: ["GET"], route: "/users/*/surveys/answers" }], permissions)
        ? {
            id: "survey-answers",
            title: translate(language, "PAGES.USER.TAB.SURVEY_ANSWERS"),
            content: <UserSurveyAnswers language={language} userId={Number(params.user_id)} />,
          }
        : undefined,
    ];
    return tabsData.filter((elem) => !!elem) as TabData[];
  };

  const addCohortToUser = () => {
    setEditedUserData((userData: { user_cohorts: any[] }) => ({
      ...userData,
      user_cohorts: [...userData.user_cohorts, {}],
    }));
  };

  const deleteCohortToUser = (index: number) => {
    setEditedUserData((userData: { user_cohorts: any[] }) => {
      let new_user_cohorts = [...userData.user_cohorts];
      new_user_cohorts.splice(index, 1);
      return {
        ...userData,
        user_cohorts: new_user_cohorts,
      };
    });
  };

  const hasRemovedCohorts = () => {
    const editedCohortIds = ((editedUserData as any).user_cohorts ?? [])
      .filter(Boolean)
      .map((cohort: any) => cohort.id);
    return ((userData as any).user_cohorts ?? []).some((cohort: any) => !editedCohortIds.includes(cohort.id));
  };

  const refreshUserBlock = (editedResources: string[]) => {
    const resourceRefreshers: Record<string, Array<() => void>> = {
      user: [() => refreshUserDetails(params.user_id!, setUserData, setLoaded)],
      cohorts: [
        () => refreshUserCohorts(params.user_id!, setUserData, setLoaded),
        () => refreshUserLessons(params.user_id!, setUserData, setLoaded),
      ],
      flags: [() => refreshUserFlags(params.user_id!, setUserData, setLoaded)],
    };

    const refreshers = editedResources.flatMap((resource) => resourceRefreshers[resource] || []);
    refreshers.forEach((refresh) => refresh());
  };

  const _build_edition_modale_tabs = (user_data: any) => {
    const tabsData: TabData[] = [
      {
        id: "information",
        title: translate(language, "PAGES.USER.USER_MODAL.INFORMATION"),
        content: (
          <div className="xs-regular px-3">
            <h3 className="mb-3 m-regular border-bottom">{translate(language, "PAGES.USER.USER_MODAL.TITLE")}</h3>
            <Row className="py-3">
              <Col className="border-end">
                <Row className="mb-3">
                  <Col>
                    <FormLabel htmlFor="user-first-name">
                      {translate(language, "PAGES.USER.USER_MODAL.FIRST_NAME")}
                    </FormLabel>
                    <FormControl
                      className="xs-regular"
                      id="user-first-name"
                      type="text"
                      value={user_data.first_name}
                      onChange={(event) =>
                        setEditedUserData((previous_user_data) => ({
                          ...previous_user_data,
                          first_name: event.target.value,
                        }))
                      }
                    />
                  </Col>
                  <Col>
                    <FormLabel htmlFor="user-last-name">
                      {translate(language, "PAGES.USER.USER_MODAL.LAST_NAME")}
                    </FormLabel>
                    <FormControl
                      className="xs-regular"
                      id="user-last-name"
                      type="text"
                      value={user_data.last_name}
                      onChange={(event) =>
                        setEditedUserData((previous_user_data) => ({
                          ...previous_user_data,
                          last_name: event.target.value,
                        }))
                      }
                    />
                  </Col>
                </Row>
                <FormLabel htmlFor="user-email">{translate(language, "PAGES.USER.USER_MODAL.EMAIL")}</FormLabel>
                <FormControl
                  className="xs-regular"
                  id="user-email"
                  type="text"
                  value={user_data.email}
                  onChange={(event) =>
                    setEditedUserData((previous_user_data) => ({ ...previous_user_data, email: event.target.value }))
                  }
                />
              </Col>
              <Col>
                <Row>
                  <Col>
                    <FormLabel htmlFor="user-start-date">
                      {translate(language, "PAGES.USER.USER_MODAL.START_DATE")}
                    </FormLabel>
                    <FormControl
                      className="xs-regular"
                      id="user-start-date"
                      type="datetime-local"
                      defaultValue={displayDate(user_data.dt_start)}
                      onBlur={(event) => {
                        setEditedUserData((previous_user_data) => ({
                          ...previous_user_data,
                          dt_start: getUTCTime(event.target.value),
                        }));
                      }}
                    />
                  </Col>
                  <Col>
                    <FormLabel htmlFor="user-end-date">
                      {translate(language, "PAGES.USER.USER_MODAL.END_DATE")}
                    </FormLabel>
                    <FormControl
                      className="xs-regular"
                      id="user-end-date"
                      type="datetime-local"
                      defaultValue={displayDate(user_data.dt_end)}
                      onBlur={(event) =>
                        setEditedUserData((previous_user_data) => ({
                          ...previous_user_data,
                          dt_end: getUTCTime(event.target.value),
                        }))
                      }
                    />
                  </Col>
                </Row>
              </Col>
            </Row>
          </div>
        ),
      },
      {
        id: "cohorts",
        title: translate(language, "PAGES.USER.USER_MODAL.COHORT"),
        content: (
          <div className="xs-regular px-3">
            <h3 className="mb-3 m-regular border-bottom">{translate(language, "PAGES.USER.USER_MODAL.COHORT")}</h3>
            <div>
              {user_data.user_cohorts.map((user_cohort: any, index: number) => (
                <div className="xs-regular" key={user_cohort?.id ?? `new-cohort-${index}`}>
                  {index > 0 && <hr />}
                  <div className="d-flex">
                    <MultiSelectLiveDropdown
                      category={["cohorts"]}
                      label={user_cohort?.name || translate(language, "PAGES.USER.USER_MODAL.SELECT_COHORT")}
                      language={language}
                      toggleClass="s-regular me-2"
                      isSingleSelect
                      initialSelectedItems={user_cohort?.id ? [{ value: user_cohort, category: "cohorts" }] : []}
                      onConfirm={(selectedItems: any[]) => {
                        const selectedCohort = selectedItems[0]?.value || null;
                        setEditedUserData((userData: any) => {
                          const new_user_cohorts = [...userData.user_cohorts];
                          new_user_cohorts[index] = selectedCohort;
                          return {
                            ...userData,
                            user_cohorts: new_user_cohorts,
                          };
                        });
                      }}
                      clearItemsOnSend={false}
                    />
                    <DstButton
                      value="Delete cohort"
                      variant="danger"
                      clickFunction={() => deleteCohortToUser(index)}
                      disabled={(editedUserData as { user_cohorts: any[] }).user_cohorts.length === 1}
                    />
                  </div>
                  {user_cohort && (
                    <Row className="my-3">
                      <Col>
                        <h3 className="m-regular">{translate(language, "PAGES.USER.USER_MODAL.FORMAT")}</h3>
                        <p className="silver-gray-font">{user_cohort.intensity}</p>
                      </Col>
                      <Col>
                        <h3 className="m-regular">{translate(language, "PAGES.USER.USER_MODAL.TIME")}</h3>
                        <p className="silver-gray-font">
                          {new Date(user_cohort.dt_start).toLocaleDateString()} -{" "}
                          {new Date(user_cohort.dt_end).toLocaleDateString()}
                        </p>
                      </Col>
                      <Col>
                        <h3 className="m-regular">{translate(language, "PAGES.USER.USER_MODAL.NEED_PROJECT")}</h3>
                        <p className="silver-gray-font">{user_cohort.need_project ? <CheckIcon /> : <CrossIcon />}</p>
                      </Col>
                      <Col>
                        <h3 className="m-regular">{translate(language, "PAGES.USER.USER_MODAL.LANGUAGE")}</h3>
                        <p className="silver-gray-font">{user_cohort.language}</p>
                      </Col>
                    </Row>
                  )}
                </div>
              ))}
              <DstButton
                variant="secondary"
                value={translate(language, "PAGES.USER.USER_MODAL.ADD_COHORT")}
                clickFunction={addCohortToUser}
              />
            </div>
          </div>
        ),
      },
      {
        id: "badges",
        title: translate(language, "PAGES.USER.USER_MODAL.BADGE"),
        content: (
          <div className="xs-regular px-3">
            <h3 className="mb-3 m-regular border-bottom">{translate(language, "PAGES.USER.USER_MODAL.FLAGS")}</h3>
            <div className="py-3">
              {flags.map((flag: any, index: number) => (
                <FormCheck
                  key={`flag-${index}`}
                  id={`flag-${flag.id}`}
                  type="checkbox"
                  inline
                  label={<label htmlFor={`flag-${flag.id}`}>{flag.name}</label>}
                  checked={user_data.user_flags?.map((user_flag: any) => user_flag.name).includes(flag.name)}
                  onChange={(event) => {
                    setEditedUserData((previous_user_data) => ({
                      ...previous_user_data,
                      user_flags: event.target.checked
                        ? [...(previous_user_data as any)?.user_flags, flag]
                        : [...(previous_user_data as any)?.user_flags.filter((element: any) => element.id !== flag.id)],
                    }));
                  }}
                />
              ))}
            </div>
            <h3 className="mb-3 m-regular border-bottom">{translate(language, "PAGES.USER.USER_MODAL.DISABILITY")}</h3>
            <div className="py-3">
              <FormCheck
                type="checkbox"
                id="badge-extratime"
                label={
                  <label htmlFor="badge-extratime">{translate(language, "PAGES.USER.USER_BLOCK.EXTRA_TIME")}</label>
                }
                checked={user_data.has_extra_time}
                onChange={(event) =>
                  setEditedUserData((previous_user_data) => ({
                    ...previous_user_data,
                    has_extra_time: event.target.checked,
                  }))
                }
              />
            </div>
          </div>
        ),
      },
    ];
    return tabsData;
  };

  useEffect(() => {
    (async () => {
      setUserData({});
      setLoaded({ ...initialLoadedState });

      ApiService.get(`users/${params.user_id}`)
        .then((resp) => {
          const user = resp.data.user;
          setUserData((userData) => ({
            ...userData,
            ...user,
          }));
        })
        .catch((err) => console.error("Error fetching user:", err))
        .finally(() => {
          setLoaded((loaded) => ({ ...loaded, user: true }));
        });
      ApiService.get(`users/${params.user_id}/cohorts`)
        .then((resp) => {
          const user_cohorts = resp.data.cohorts;
          setUserData((userData) => ({
            ...userData,
            user_cohorts,
          }));
        })
        .catch((err) => console.error("An error occurred for fetching user's cohorts", err))
        .finally(() => setLoaded((loaded) => ({ ...loaded, cohorts: true })));
      ApiService.get(`users/${params.user_id}/projects`)
        .then((resp) => {
          const user_projects = resp.data.projects;
          setUserData((userData) => ({
            ...userData,
            user_projects,
          }));
        })
        .catch((err) => console.error("An error occurred for fetching user's projects", err))
        .finally(() => setLoaded((loaded) => ({ ...loaded, projects: true })));
      refreshUserLessons(params.user_id!, setUserData, setLoaded);
      if (validateUserPermission({ methods: ["GET"], route: "/users/*/certifications" }, permissions)) {
        ApiService.get(`users/${params.user_id}/certifications`)
          .then((resp) => {
            const user_certifs = resp.data.certifications;
            setUserData((userData) => ({
              ...userData,
              user_certifs,
            }));
            setLoaded((loaded) => ({ ...loaded, certifs: true }));
          })
          .catch((err) => console.error("An error occurred for fetching user's certifications", err));
      }
      ApiService.get(`users/${params.user_id}/flags`)
        .then((resp) => {
          const user_flags = resp.data.user_flags;
          setUserData((userData) => ({
            ...userData,
            user_flags,
          }));
          const hasApprenticeshipFlag = user_flags?.some((flag: any) => flag.name === "Apprenticeship");
          if (
            hasApprenticeshipFlag &&
            validateUserPermission([{ methods: ["GET"], route: "/users/*/documents" }], permissions)
          )
            refreshUserDocuments();
          else setLoaded((loaded) => ({ ...loaded, documents: true }));
        })
        .catch((err) => {
          console.error("An error occurred for fetching user's flags", err);
          setLoaded((loaded) => ({ ...loaded, documents: true }));
        })
        .finally(() => setLoaded((loaded) => ({ ...loaded, flags: true })));
      if (validateUserPermission({ methods: ["GET"], route: "/users/*/notes" }, permissions))
        ApiService.get(`users/${params.user_id}/notes?fetch_author=true`)
          .then((resp) => {
            const user_notes = resp.data.notes;
            setUserData((userData) => ({
              ...userData,
              user_notes,
            }));
            setLoaded((loaded) => ({ ...loaded, notes: true }));
          })
          .catch((err) => console.error("An error occurred for fetching user's notes", err));
    })();
  }, [params.user_id, permissions, refreshUserDocuments]);

  useEffect(() => {
    if (showEditionModale && modaleLoaded) {
      setEditedUserData(userData);
      setEditionReady(true);
    } else {
      setEditionReady(false);
    }
  }, [showEditionModale, modaleLoaded, userData]);

  if (loaded.user && Object.keys(userData).length === 0) {
    return (
      <div className="text-center mt-3">
        <h2>{translate(language, "PAGES.USER.USER_NOT_FOUND.TITLE")}</h2>
        <DstButton
          value={translate(language, "PAGES.USER.USER_NOT_FOUND.BUTTON")}
          clickFunction={() => navigate("/users")}
        />
      </div>
    );
  }

  return (
    <div className="d-flex">
      {loaded.user && (
        <Helmet>
          <title>
            User - {(userData as any).first_name} {(userData as any).last_name}
          </title>
        </Helmet>
      )}

      <UserBlock
        edit
        editFunction={() => {
          setShowEditionModale(true);
          setEditedUserData({ ...userData });
          fetchFlags(setFlags, () => setModaleLoaded(true));
        }}
        loginAsFunction={loginAs(params.user_id!)}
        logoutFunction={logout(params.user_id!)}
        loadedDocuments={loaded.documents}
        mock={!(loaded.user && loaded.flags && loaded.cohorts)}
        user={userData}
        language={language}
        refreshUserCertifications={refreshUserCertifications}
        refreshUserDocuments={
          validateUserPermission([{ methods: ["GET"], route: "/users/*/documents" }], permissions)
            ? refreshUserDocuments
            : undefined
        }
        refreshUserExams={refreshUserExams}
      />
      <DstTabs
        tabsData={_build_tabs(userData)}
        variant="primary"
        classes="ps-3 flex-grow-1 user-tabs"
        activeKey={activeKey}
        onSelect={(tabKey: string | null) => setActiveKey(tabKey!)}
      />

      {loaded.user && (
        <Popup
          show={showEditionModale}
          onClose={() => setShowEditionModale(false)}
          modalTitle={`Edit ${(userData as any)?.first_name} ${(userData as any)?.last_name}`}
          size="xl"
          fullscreen="lg-down"
          scrollable={true}
        >
          {modaleLoaded ? (
            <React.Fragment>
              <DstTabs
                tabsData={_build_edition_modale_tabs(editedUserData)}
                variant="secondary"
                activeKey={activeModalKey}
                onSelect={(tabKey: string | null) => setActiveModalKey(tabKey!)}
              />
              <DstButton
                btnClass="mt-3 mx-auto d-block"
                value={translate(language, "PAGES.USER.USER_MODAL.CONFIRM")}
                loading={editionLoading}
                disabled={!editionReady || JSON.stringify(userData) === JSON.stringify(editedUserData)}
                clickFunction={() => {
                  if (hasRemovedCohorts()) {
                    setShowRemoveUsersFromCohortConfirm(true);
                  } else {
                    updateUser(userData, editedUserData, setEditionLoading, refreshUserBlock, language, user_id);
                  }
                }}
              />
            </React.Fragment>
          ) : (
            <Spinner className="d-block blue-font mb-3 mx-auto" />
          )}
        </Popup>
      )}
      <DstConfirm
        show={showRemoveUsersFromCohortConfirm}
        onHide={() => setShowRemoveUsersFromCohortConfirm(false)}
        onConfirm={(note?: string) => {
          setShowRemoveUsersFromCohortConfirm(false);
          updateUser(userData, editedUserData, setEditionLoading, refreshUserBlock, language, user_id, note);
        }}
        title={translate(language, "PAGES.USER.USER_BLOCK.REMOVE_CONFIRM.TITLE")}
        messageExplain={translate(language, "PAGES.USER.USER_BLOCK.REMOVE_CONFIRM.EXPLAIN")}
        messageConfirm={translate(language, "PAGES.USER.USER_BLOCK.REMOVE_CONFIRM.MESSAGE")}
        confirmTextButton={translate(language, "PAGES.USER.USER_BLOCK.REMOVE_CONFIRM.BUTTON")}
        showNoteInput
        language={language}
        noteItems={_REMOVE_USER_NOTE_KEYS.map(({ id, key }) => ({
          id,
          label: translate(language, key),
        }))}
        noteItemsPlaceholder={translate(language, "PAGES.USER.USER_BLOCK.REMOVE_CONFIRM.NOTE_ITEMS_PLACEHOLDER")}
        noteLabel={translate(language, "PAGES.USER.USER_BLOCK.REMOVE_CONFIRM.NOTE_LABEL")}
        notePlaceholder={translate(language, "PAGES.USER.USER_BLOCK.REMOVE_CONFIRM.NOTE_PLACEHOLDER")}
      />
    </div>
  );
};

export default User;
